import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FaSave } from 'react-icons/fa';
import { IDPProvider, useIDP } from '../../../IndividualDevelopmentPlans/components/contexts/IDPContext';
import { IDPFormContent } from '../../../IndividualDevelopmentPlans/components/IDPForm';
import LoadingSpinner from '../../../common/LoadingSpinner';
import EmptyState from '../../../common/EmptyState';

/**
 * IDPModalContent - Inner component that can access IDPContext
 */
const IDPModalContent = ({ onClose, onSave, readonly, initialTab }) => {
  const { goalData, isDirty, isSaving, saveGoal, isLoading, error } = useIDP();

  const handleSave = async () => {
    const success = await saveGoal();
    if (success && onSave) {
      onSave();
    }
  };

  if (isLoading) {
    return (
      <>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Development Goal</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LoadingSpinner message="Loading development goal..." />
        </DialogContent>
      </>
    );
  }

  if (error) {
    return (
      <>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Development Goal</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            flex: '1 1 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ width: '100%' }}>
            <EmptyState title="Error Loading Goal" description={error} />
          </Box>
        </DialogContent>
      </>
    );
  }

  return (
    <>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" noWrap sx={{ flex: 1, mr: 2 }}>
            {goalData?.title || 'Development Goal'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <IDPFormContent onBack={onClose} hideHeader={true} inModal={true} initialTab={initialTab} />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #E2E8F0' }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
        {!readonly && (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!isDirty || isSaving}
            startIcon={<FaSave />}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        )}
      </DialogActions>
    </>
  );
};

/**
 * IDPViewModal - Modal wrapper for viewing/editing IDP goals
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal closes
 * @param {Function} props.onSave - Callback when goal is saved successfully
 * @param {string} props.goalId - The IDP goal ID to display
 * @param {boolean} props.readonly - Whether the form is read-only (default: false)
 * @param {string} props.initialTab - Initial tab to display ('GENERAL', 'ACTIONS', 'HISTORY')
 */
const IDPViewModal = ({ open, onClose, onSave, goalId, readonly = false, initialTab = 'GENERAL' }) => {
  if (!open || !goalId) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          height: '85vh',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <IDPProvider goalId={goalId} readonly={readonly}>
        <IDPModalContent onClose={onClose} onSave={onSave} readonly={readonly} initialTab={initialTab} />
      </IDPProvider>
    </Dialog>
  );
};

export default IDPViewModal;
